/**
 * EventOperationPage — the host dashboard for one event.
 * docs/plans/2026-09-host-event-edit/handoff/README.md (Sept 2026 redesign).
 *
 * The site-wide top bar → the event header (← MY EVENTS · HOST DASHBOARD + status pill ·
 * title · when line · stat pills) → the sticky pill tab bar `GUEST LIST · APPLICATIONS ·
 * UPDATES · ASK MORE · DOOR · MONEY · EDIT` → a 1280px grid: the tab's white cards and, on
 * desktop, the sticky 360px host rail (GREENLIGHT · DOOR MODE · HOST ACTIONS · PAYOUT); on
 * mobile the rail stacks under the tab. Tabs are client state with `?tab=` deep links
 * (`&step=` for the EDIT wizard). Every number is counted off the applications list; the
 * 30s poll stays. Saves, exports and publishes answer with the black toast.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { webOrigin } from '../utils/brand';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ErrorState, ShareSheet } from '../components';
import { eventCardUrl } from '../utils/shareCard';
import { SurfacePage } from '../components/surface';
import useIsMobile from '../hooks/useIsMobile';
import useTopBarHeight from '../hooks/useTopBarHeight';
import useMediaQuery from '../utils/useMediaQuery';
import { eventService, applicationService, mimeService, ticketService, refundService, announcementService } from '../services';
import { useAuth, useUser, useDialog } from '../context';
import MimePanel from '../components/MimePanel';
import EventAnnouncementBoard from '../components/EventAnnouncementBoard';
import HostGuestList, { HostGuestListOverlay } from '../components/host/HostGuestList';
import HostApplications from '../components/host/HostApplications';
import HostDoor from '../components/host/HostDoor';
import HostMoney from '../components/host/HostMoney';
import HostEdit from '../components/host/HostEdit';
import HostRail from '../components/host/HostRail';
import HostEventHeader from '../components/host/HostEventHeader';
import HostTabBar from '../components/host/HostTabBar';
import BroadcastModal from '../components/host/BroadcastModal';
import { Btn, Card, Toast, useToast } from '../components/host/kit';
import { bucketApplications, opsStats, guestRows, buildGuestCsv, fallbackMoney, fundingProgress, ACTION_STATUSES, guestName } from '../components/host/hostOpsModel';
import { downloadText, slugFile } from '../components/host/hostBits';

const TAB_KEYS = ['guests', 'applications', 'updates', 'mime', 'door', 'money', 'edit'];
const DAY_MS = 24 * 60 * 60 * 1000;

const clampStep = (v) => Math.min(5, Math.max(1, parseInt(v, 10) || 1));

export default function EventOperationPage() {
  const navigate = useNavigate();
  const { id: eventId } = useParams();
  const [params, setParams] = useSearchParams();
  const mobile = useIsMobile();
  const phone = useMediaQuery('(max-width: 639px)');
  const topH = useTopBarHeight();
  const { isAuthenticated } = useAuth();
  const { profile } = useUser();
  const { showAlert, showConfirm } = useDialog();
  const { toast, say } = useToast();

  // Data
  const [event, setEvent] = useState(null);
  const [gatheringInfo] = useState(null); // a pass event redirects to the gathering manager before this is ever needed
  const [applications, setApplications] = useState([]);
  const [moneyDto, setMoneyDto] = useState(null);
  const [mimeSummary, setMimeSummary] = useState(null);
  const [announcementCount, setAnnouncementCount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Action state
  const [actionLoading, setActionLoading] = useState({});
  const [busy, setBusy] = useState({});
  const [shareOpen, setShareOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [guestListOpen, setGuestListOpen] = useState(false);
  const [remindBusyId, setRemindBusyId] = useState(null);
  const [remindSent, setRemindSent] = useState({});
  const [remindAllBusy, setRemindAllBusy] = useState(false);
  const [editStep, setEditStep] = useState(() => clampStep(params.get('step')));

  const setBusyKey = (k, v) => setBusy((b) => ({ ...b, [k]: v }));

  // Tabs are client state; ?tab= deep-links (drop the param for the first tab).
  const tabParam = params.get('tab');
  const tab = TAB_KEYS.includes(tabParam) ? tabParam : 'guests';
  const setTab = useCallback((t) => {
    const n = new URLSearchParams(params);
    if (t === 'guests') n.delete('tab'); else n.set('tab', t);
    if (t !== 'edit') n.delete('step');
    setParams(n, { replace: true });
  }, [params, setParams]);

  // Fetch event + applications (the source of every count on the page).
  const fetchData = useCallback(async (showRefreshing = false) => {
    if (!showRefreshing) setIsLoading(true);
    setError(null);
    try {
      const [eventResult, applicationsResult] = await Promise.all([
        eventService.getEvent(eventId),
        applicationService.getEventApplications(eventId),
      ]);
      if (!eventResult.success) throw new Error(eventResult.error || 'Failed to load event');
      setEvent(eventResult.data);
      if (applicationsResult.success) setApplications(applicationsResult.data || []);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  // Secondary reads: the money summary (404 → fallback), the MIME summary (tab count +
  // rail) and the announcement count. Each fails soft.
  const fetchAux = useCallback(async () => {
    if (!eventId) return;
    const [m, s, a] = await Promise.all([
      eventService.getEventMoney(eventId),
      mimeService.getEventSummary(eventId),
      announcementService.list(eventId),
    ]);
    setMoneyDto(m.success && m.data ? m.data : null);
    if (s.success) setMimeSummary(s.data);
    if (a.success) setAnnouncementCount((a.data || []).length);
  }, [eventId]);

  useEffect(() => { if (eventId) fetchData(); }, [eventId, fetchData]);
  useEffect(() => { if (event?.id) fetchAux(); }, [event?.id, fetchAux]);

  // A hidden gathering pass event is managed on the gathering's own manager now; an old
  // /host/events/{passEventId} link lands there (replace, so Back still works). Real child
  // events keep this page.
  useEffect(() => {
    const gid = event?.isGatheringPass ? event?.gatheringId : null;
    if (!gid) return;
    navigate(`/gatherings/${gid}/manage`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.isGatheringPass, event?.gatheringId]);

  // 30-second poll so door / MIME / sales counts never drift.
  useEffect(() => {
    if (!eventId || isLoading) return undefined;
    const id = setInterval(() => { fetchData(true); fetchAux(); }, 30000);
    return () => clearInterval(id);
  }, [eventId, isLoading, fetchData, fetchAux]);

  // ── Derived ──
  const buckets = useMemo(() => bucketApplications(applications), [applications]);
  const stats = useMemo(() => opsStats({ event, applications, money: moneyDto }), [event, applications, moneyDto]);
  const rows = useMemo(() => guestRows(applications), [applications]);
  const funding = useMemo(() => fundingProgress(event, applications), [event, applications]);
  const moneyView = useMemo(() => moneyDto || (event ? fallbackMoney({ event, applications }) : null), [moneyDto, event, applications]);
  const showActions = ACTION_STATUSES.includes(event?.status);
  const attendeeCount = buckets.attending.length;
  const viewerIsOwner = !!(event && profile && event.hostId === profile.id);
  const viewerIsCoHost = !!(event && profile && Array.isArray(event.coHosts) && event.coHosts.some((c) => c.userId === profile.userId));

  // ── Handlers (every one the old page had) ──
  const handleAccept = async (application) => {
    setActionLoading((prev) => ({ ...prev, [application.id]: 'accept' }));
    try {
      const result = await applicationService.acceptApplication(application.id);
      if (result.success) setApplications((prev) => prev.map((a) => (a.id === application.id ? { ...a, status: 'accepted' } : a)));
      else throw new Error(result.error || 'Failed to accept application');
    } catch (err) {
      showAlert({ title: 'ERROR', message: err.message, type: 'error' });
    } finally {
      setActionLoading((prev) => ({ ...prev, [application.id]: null }));
    }
  };

  const handleDecline = async (application) => {
    setActionLoading((prev) => ({ ...prev, [application.id]: 'decline' }));
    try {
      const result = await applicationService.declineApplication(application.id);
      if (result.success) setApplications((prev) => prev.map((a) => (a.id === application.id ? { ...a, status: 'declined' } : a)));
      else throw new Error(result.error || 'Failed to decline application');
    } catch (err) {
      showAlert({ title: 'ERROR', message: err.message, type: 'error' });
    } finally {
      setActionLoading((prev) => ({ ...prev, [application.id]: null }));
    }
  };

  const handleReinvite = async (application) => {
    setActionLoading((prev) => ({ ...prev, [application.id]: 'reinvite' }));
    try {
      const result = await applicationService.reinviteApplication(application.id);
      if (result.success) setApplications((prev) => prev.filter((a) => a.id !== application.id));
      else throw new Error(result.error || 'Failed to reinvite');
    } catch (err) {
      showAlert({ title: 'ERROR', message: err.message, type: 'error' });
    } finally {
      setActionLoading((prev) => ({ ...prev, [application.id]: null }));
    }
  };

  const handleViewCredentials = (application) => {
    if (!application?.guest?.userId) return;
    navigate(`/users/${application.guest.userId}?applicationId=${application.id}`);
  };

  const handleMessageFirst = (application) => {
    if (!application?.guest?.userId) return;
    navigate(`/messages?with=${application.guest.userId}&event=${eventId}`);
  };

  const handleGreenlight = async () => {
    const confirmed = await showConfirm({
      title: 'GREENLIGHT EVENT',
      message: 'Are you sure you want to greenlight this event? This will charge all accepted applicants.',
      confirmLabel: 'GREENLIGHT',
      variant: 'primary',
    });
    if (!confirmed) return;
    setBusyKey('greenlight', true);
    try {
      const result = await eventService.greenlightEvent(eventId);
      if (result.success) {
        setEvent((prev) => ({ ...prev, status: 'processing_greenlight' }));
        showAlert({ title: 'SUCCESS', message: 'Event greenlighted successfully! Payments are being processed.', type: 'success' });
      } else throw new Error(result.error || 'Failed to greenlight event');
    } catch (err) {
      showAlert({ title: 'ERROR', message: err.message, type: 'error' });
    } finally {
      setBusyKey('greenlight', false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await showConfirm({
      title: 'DELETE EVENT',
      message: 'Are you sure you want to delete this event? This cannot be undone.',
      confirmLabel: 'DELETE',
    });
    if (!confirmed) return;
    setBusyKey('delete', true);
    try {
      const result = await eventService.deleteEvent(eventId);
      if (!result.success) throw new Error(result.error || 'Failed to delete event');
      navigate('/host');
    } catch (err) {
      showAlert({ title: 'ERROR', message: err.message || 'Failed to delete event.', type: 'error' });
    } finally {
      setBusyKey('delete', false);
    }
  };

  const handleCancel = async () => {
    const isFree = (event?.ticketPriceCents || 0) === 0;
    // A greenlit event has already collected money and taken the platform fee, so the
    // warning is stronger — funding succeeded and guests are expecting it to happen.
    const isGreenlit = event?.status === 'greenlit' || event?.status === 'processing_greenlight';
    const confirmed = await showConfirm({
      title: isGreenlit ? 'CANCEL GREENLIT EVENT' : 'CANCEL EVENT',
      message: isFree
        ? 'Cancel this event? All guests will be notified. This cannot be undone.'
        : (isGreenlit
          ? 'This event is already greenlit and funded. Cancelling refunds every customer in '
            + 'full to their original payment method — card payments back to the card, wallet '
            + 'payments back to the wallet — and notifies all guests. This cannot be undone.'
          : 'Cancel this event? Every customer will be refunded automatically to their original payment method — card payments back to the card, wallet payments back to the wallet — and all guests will be notified. This cannot be undone.'),
      confirmLabel: isFree ? 'CANCEL EVENT' : 'CANCEL & REFUND ALL',
      cancelLabel: 'KEEP EVENT',
      variant: 'danger',
    });
    if (!confirmed) return;
    setBusyKey('cancel', true);
    try {
      const result = await eventService.cancelEvent(eventId);
      if (!result.success) throw new Error(result.error || 'Failed to cancel event');
      setEvent((prev) => ({ ...prev, status: 'canceled' }));
      await showAlert({ title: 'EVENT CANCELED', message: isFree ? 'The event was canceled and all guests have been notified.' : 'The event was canceled and all customers have been refunded to their original payment method.', type: 'success' });
    } catch (err) {
      showAlert({ title: 'ERROR', message: err.message || 'Failed to cancel event.', type: 'error' });
    } finally {
      setBusyKey('cancel', false);
    }
  };

  const handleDuplicate = async () => {
    setBusyKey('duplicate', true);
    try {
      const result = await eventService.duplicateEvent(eventId);
      if (result.success) navigate(`/host/events/${result.data.id}/edit`);
      else throw new Error(result.error || 'Failed to duplicate event');
    } catch (err) {
      showAlert({ title: 'ERROR', message: err.message || 'Failed to duplicate event.', type: 'error' });
    } finally {
      setBusyKey('duplicate', false);
    }
  };

  const handlePublish = async () => {
    setBusyKey('publish', true);
    try {
      const result = await eventService.publishEvent(eventId);
      if (result.success) {
        setEvent((prev) => ({ ...prev, status: prev?.fundingType === 'party_starter' ? 'funding' : 'selling' }));
        say('PUBLISHED · YOUR EVENT IS LIVE');
        fetchData(true);
      } else showAlert({ title: 'ERROR', message: result.error?.message || result.error || 'Failed to publish event.', type: 'error' });
    } finally {
      setBusyKey('publish', false);
    }
  };

  const handleResend = async (app) => {
    try {
      await ticketService.resendTicket(app.ticketCode);
      await showAlert({ title: 'SENT', message: `Ticket email resent to ${app.guestCheckoutEmail || app.guestEmail || guestName(app)}`, type: 'success' });
    } catch {
      await showAlert({ title: 'ERROR', message: 'Failed to resend ticket email', type: 'error' });
    }
  };

  // Per-customer refund — routes each tender back to its origin: card-paid back to the
  // card, wallet-paid back to the wallet. The host doesn't have to know how the guest paid.
  const handleRefund = async (app) => {
    const who = app.guestCheckoutName || app.guest?.displayName || 'this guest';
    const paidByCard = (app.cardPortionCents || 0) > 0;
    const paidByWallet = (app.balancePortionCents || 0) > 0;
    const dest = paidByCard && paidByWallet ? 'their original card and wallet' : paidByCard ? 'their original card' : 'their wallet';
    const ok = await showConfirm({
      title: 'REFUND CUSTOMER',
      message: `Refund ${who} to ${dest}? The $1 service fee, GST, and processing fee aren't refundable.`,
      confirmLabel: 'REFUND',
      cancelLabel: 'CANCEL',
      variant: 'danger',
    });
    if (!ok) return;
    const r = await refundService.issueRefund(app.id, 'auto', 'Host-approved refund');
    if (r.success) {
      await showAlert({ title: 'REFUNDED', message: `Refund issued to ${dest}.`, type: 'success' });
      fetchData(true); fetchAux();
    } else {
      await showAlert({ title: 'ERROR', message: r.error || 'Refund failed', type: 'error' });
    }
  };

  const handleRemind = async (app) => {
    setRemindBusyId(app.id);
    const r = await applicationService.sendPaymentReminder(app.id);
    setRemindBusyId(null);
    if (r.success) { setRemindSent((prev) => ({ ...prev, [app.id]: true })); fetchData(true); }
    else showAlert({ title: 'ERROR', message: r.error || 'Could not send reminder', type: 'error' });
  };

  const handleRemindAll = async () => {
    setRemindAllBusy(true);
    try {
      for (const app of buckets.pendingPayment) {
        // Skip rows nudged within 24h — the server would reject; no point round-tripping.
        const last = app.lastPaymentReminderAt ? new Date(app.lastPaymentReminderAt).getTime() : 0;
        if (Date.now() - last < DAY_MS || remindSent[app.id]) continue;
        // eslint-disable-next-line no-await-in-loop
        await handleRemind(app);
      }
    } finally {
      setRemindAllBusy(false);
    }
  };

  const handleReviewTrick = async (trick, status) => {
    await eventService.reviewPartyTrick(trick.id, status);
    fetchData(true);
  };

  const handleExport = () => {
    downloadText(`${slugFile(event?.title)}-guest-list.csv`, buildGuestCsv(applications));
    say('GUEST LIST EXPORTED · CSV');
  };

  const publicUrl = `/events/${event?.inviteCode || eventId}`;
  const goPublic = () => navigate(publicUrl);
  const backTarget = event?.isGatheringPass && event?.gatheringId ? `/gatherings/${event.gatheringId}` : '/host?tab=events';

  const goEditStep = useCallback((n) => {
    const step = clampStep(n);
    setEditStep(step);
    const next = new URLSearchParams(params);
    next.set('tab', 'edit');
    if (step === 1) next.delete('step'); else next.set('step', String(step));
    setParams(next, { replace: true });
  }, [params, setParams]);

  // ── Access & load states ──
  if (!isAuthenticated) {
    return (
      <div className="hev" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <Card center gap="12" style={{ maxWidth: 420, width: '100%' }}>
          <span className="hev-sec-title">AUTHENTICATION REQUIRED</span>
          <span className="hev-sec-sub">Please sign in to view event operations.</span>
          <Btn onClick={() => navigate('/login')}>SIGN IN</Btn>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <SurfacePage bare showTopBarOnMobile>
        <div className="hev" data-testid="ops-loading">
          <div className="hev-hero"><div className="hev-hero-inner" style={{ minHeight: 160 }} /></div>
          <div className="hev-main"><div className="hev-col"><Card style={{ minHeight: 220 }} /><Card style={{ minHeight: 120 }} /></div></div>
        </div>
      </SurfacePage>
    );
  }

  if (error) {
    return (
      <SurfacePage bare showTopBarOnMobile>
        <div className="hev" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <ErrorState message={error} onRetry={() => fetchData()} />
        </div>
      </SurfacePage>
    );
  }

  // The primary host OR any co-host may view this page; editing is separately gated by
  // viewerCanEdit and owner-only controls are gated inside the panels.
  if (event && profile && !viewerIsOwner && !viewerIsCoHost) {
    return (
      <SurfacePage bare showTopBarOnMobile>
        <div className="hev" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} data-testid="access-denied">
          <Card center gap="12" style={{ maxWidth: 420, width: '100%' }}>
            <span className="hev-sec-title">ACCESS DENIED</span>
            <span className="hev-sec-sub">You do not have permission to manage this event.</span>
            <Btn onClick={() => navigate('/guest')}>GO TO DASHBOARD</Btn>
          </Card>
        </div>
      </SurfacePage>
    );
  }

  // ── Header pieces ──
  const title = event?.isGatheringPass
    ? (gatheringInfo?.name || event?.title?.replace(/ — Entry$/, '') || 'Gathering')
    : (event?.title || 'Event');

  const tabs = [
    { key: 'guests', label: 'GUEST LIST' },
    { key: 'applications', label: 'APPLICATIONS', count: buckets.pending.length || undefined },
    { key: 'updates', label: 'UPDATES', count: announcementCount || undefined },
    { key: 'mime', label: 'ASK MORE', count: mimeSummary?.activeRound ? 1 : undefined },
    { key: 'door', label: 'DOOR' },
    { key: 'money', label: 'MONEY' },
    { key: 'edit', label: 'EDIT' },
  ];

  const rowProps = { showActions, onRowClick: handleViewCredentials, onResend: handleResend, onRefund: handleRefund, onRemind: handleRemind, remindBusyId, remindSent };

  const tabContent = (() => {
    switch (tab) {
      case 'applications':
        return (
          <HostApplications event={event} buckets={buckets} mobile={mobile} showActions={showActions} actionLoading={actionLoading}
            onAccept={handleAccept} onDecline={handleDecline} onReinvite={handleReinvite} onMessage={handleMessageFirst} onView={handleViewCredentials} onReviewTrick={handleReviewTrick} />
        );
      case 'updates':
        return <EventAnnouncementBoard eventId={eventId} isHost audienceCount={stats.sold} mobile={mobile} onRowsChange={(list) => setAnnouncementCount(list.length)} variant="surface" />;
      case 'mime':
        return <MimePanel eventId={eventId} mimeService={mimeService} acceptedCount={buckets.attending.length} eventStatus={event?.status} mobile={mobile} onSummaryChange={setMimeSummary} />;
      case 'door':
        return <HostDoor eventId={eventId} applications={applications} mobile={mobile} refreshKey={refreshKey} onCheckedIn={() => fetchData(true)} />;
      case 'money':
        return <HostMoney event={event} eventId={eventId} moneyDto={moneyView} mobile={mobile} />;
      case 'edit':
        return (
          <HostEdit event={event} eventId={eventId} mobile={mobile} canEdit={!!event?.viewerCanEdit} paidCount={event?.paidCount || buckets.paid.length} gatheringInfo={gatheringInfo}
            step={editStep} onStepChange={goEditStep}
            onSaved={async () => { await fetchData(true); fetchAux(); say('SAVED · YOUR CHANGES ARE LIVE'); }}
            onInvalid={() => say('FIX THE HIGHLIGHTED FIELDS')}
            onViewPublic={goPublic} />
        );
      default:
        return (
          <HostGuestList rows={rows} total={rows.length} stragglers={buckets.pendingPayment} checkedIn={stats.checkedIn} waitlist={stats.waitlist} mobile={mobile} showActions={showActions}
            onExport={handleExport} onMessageAll={() => setBroadcastOpen(true)} onOpenAll={() => setGuestListOpen(true)} onShare={() => setShareOpen(true)} onRemindAll={handleRemindAll} remindAllBusy={remindAllBusy} rowProps={rowProps} />
        );
    }
  })();

  const rail = (
    <HostRail event={event} mobile={mobile} funding={funding} moneyDto={moneyView} busy={busy}
      onGoTab={setTab} onGreenlight={handleGreenlight} onShare={() => setShareOpen(true)} onBroadcast={() => setBroadcastOpen(true)}
      onDuplicate={handleDuplicate} onPublish={handlePublish} onDelete={handleDelete} onCancel={handleCancel} onViewPublic={goPublic} />
  );

  const overlays = (
    <>
      <ShareSheet
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        title={event?.title || ''}
        venue={event?.venueName}
        city={event?.locationCity}
        url={`${webOrigin()}${publicUrl}`}
        inviteCode={event?.inviteCode || eventId}
        date={event?.dateTime}
        status={event?.status}
        mode={event?.mode}
        eventId={eventId}
        eventService={eventService}
        posterUrl={event?.posterUrl}
        cardUrl={eventCardUrl(event?.inviteCode)}
      />
      <BroadcastModal isOpen={broadcastOpen} onClose={() => setBroadcastOpen(false)} eventId={eventId} eventTitle={event?.title} attendeeCount={attendeeCount} />
      <HostGuestListOverlay open={guestListOpen} onClose={() => setGuestListOpen(false)} rows={rows} total={rows.length} mobile={mobile}
        note={`${stats.checkedIn} CHECKED IN · ${stats.waitlist} ON THE WAITLIST`} rowProps={rowProps} />
      <Toast text={toast} />
    </>
  );

  return (
    <SurfacePage bare showTopBarOnMobile>
      <div className="hev" style={{ '--hev-top': `${topH}px` }} data-surface={mobile ? 'mobile' : 'desktop'}>
        <HostEventHeader
          title={title}
          status={event?.status}
          dateTime={event?.dateTime}
          stats={stats}
          phone={phone}
          backLabel={event?.isGatheringPass ? 'GATHERING' : 'MY EVENTS'}
          onBack={() => navigate(backTarget)}
        />
        <HostTabBar tabs={tabs} active={tab} onChange={setTab} />
        <main className="hev-main">
          <div className="hev-col" key={tab}>{tabContent}</div>
          <aside className="hev-rail" data-testid={mobile ? 'mobile-rail' : 'sticky-rail'}>{rail}</aside>
        </main>
        {overlays}
      </div>
    </SurfacePage>
  );
}
